import google, { mapContainerWidth, mutators, noPrintClass, removeMapControl, repositionMapControl, verify } from "@churchofjesuschrist/maps-google";
import { breakSizeDefault } from "@churchofjesuschrist/maps-constants";
import { addListener, arrayEquals, buttonClass, classNames, controlClass, isFunction, noop, proxy, proxyQueue, proxyTimeout, removeListeners, removeUndefined } from "@churchofjesuschrist/maps-utils";
import MapsService, { MapProviderType } from "@churchofjesuschrist/maps-service";
import OverviewControl from "@churchofjesuschrist/maps-google-overview-control";
import { LightMapType, MapTypeId } from "@churchofjesuschrist/maps-google-styles";
import { mapTypeFilled, toHTMLString } from "@churchofjesuschrist/eden-icons-raw";
import MapTypeControlStyle from "./components/MapTypeControlStyle";
import Callout from "./components/Callout";
let initialized = false;
const defaultOptions = {};
class MapTypeControl {
  constructor(options = {}) {
    if (!initialized) {
      verify();
      construct();
      initialized = true;
    }
    this.setOptions({
      ...defaultOptions,
      ...removeUndefined(options)
    });
  }
}
const construct = () => {
  Object.assign(defaultOptions, {
    defaultMapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.HYBRID],
    style: MapTypeControlStyle.DEFAULT,
    position: google.maps.ControlPosition.RIGHT_BOTTOM
  });
  const strings = {};
  const stringsPromise = MapsService.getStrings(["maptypes", "maptypes.map", "maptypes.satellite"]).then(results => {
    Object.assign(strings, results);
  });
  Object.setPrototypeOf(MapTypeControl.prototype, Object.assign(new google.maps.MVCObject(), mutators("map", "mapTypeIds", "style", "overviewMapTypeId"), (() => {
    function onAdd() {
      let map = this.getMap();
      if (this.$map !== map) {
        onRemove.call(this);
        this.$map = map;
        this.$queueRefresh = proxyQueue(this.refresh, this, 50);
        this.$listeners = [map.addListener("maptypeid_changed", proxy(onMapTypeIdChanged, this)), map.addListener("maptypecontroloptions_changed", proxy(onMapTypeControlOptionsChanged, this)), map.addListener("resize", proxy(onResize, this))];
        if (window.ResizeObserver) {
          this.$observer = new ResizeObserver(proxy(onResize, this));
          this.$observer.observe(map.getDiv());
        }
        onMapTypeControlOptionsChanged.call(this);
        updateMode.call(this);
        updateMapTypeIds.call(this);
        this.$queueRefresh();
      }
    }
    function onRemove() {
      if (removeListeners(this.$listeners)) {
        delete this.$listeners;
      }
      if (this.$observer) {
        this.$observer.disconnect();
        delete this.$observer;
      }
      removeOverview.call(this);
      removeButton.call(this);
      removeCallout.call(this);
      delete this.$mode;
      delete this.$map;
    }
    function addOverview() {
      removeOverview.call(this);
      if (!this.$overview) {
        this.$overview = new OverviewControl();
        //TODO: title text
      }
      this.$overviewListener = this.$overview.addListener("click", proxy(onOverviewClick, this));
      this.$overview.setMap(this.getMap());
      this.$overview.bindTo("position", this);
      this.$overview.bindTo("className", this);
    }
    function removeOverview() {
      if (this.$overview) {
        this.$overview.unbindAll();
        this.$overview.setMap(null);
      }
      if (this.$overviewListener) {
        this.$overviewListener.remove();
        delete this.$overviewListener;
      }
    }
    function addButton() {
      if (!this.$div) {
        this.$div = document.createElement("div");
        let button = document.createElement("button");
        button.type = "button";
        button.className = buttonClass();
        button.innerHTML = toHTMLString(mapTypeFilled); //TODO: use new layers icon once available
        button.addEventListener("click", proxy(onButtonClick, this));
        this.$div.appendChild(button);
        stringsPromise.then(() => {
          button.title = strings["maptypes"];
        });
      }
      addCallout.call(this);
      updateClassName.call(this);
      updatePosition.call(this);
    }
    function removeButton() {
      hideCallout.call(this);
      removeMapControl(this.$map, this.$div);
    }
    function addCallout() {
      if (!this.$callout) {
        this.$callout = new Callout({
          target: this.getDiv(),
          mapTypeIds: this.getMapTypeIds(),
          map: this.getMap()
        });
        this.$listeners?.push(addListener(window, "click", proxy(onWindowClick, this)));
      }
    }
    function removeCallout() {
      if (this.$callout) {
        this.$callout.setMap(null);
        delete this.$callout;
      }
    }
    function onResize() {
      hideCallout.call(this);
      let changed = updateMode.call(this);
      if (changed) {
        this.$queueRefresh();
      }
    }
    function onWindowClick(event) {
      let div = this.getDiv();
      if (!div || !(event.target === div || div.contains(event.target))) {
        hideCallout.call(this);
      }
    }
    function onMapTypeIdChanged() {
      if (this.$mapTypeId) {
        this.$previousMapTypeId = this.$mapTypeId;
      }
      let map = this.getMap();
      if (map) {
        this.$mapTypeId = map.getMapTypeId();
      }
      this.$queueRefresh();
    }
    function onOverviewClick() {
      let mapTypesIds = this.getMapTypeIds(),
        map = this.getMap();
      if (mapTypesIds && map) {
        if (mapTypesIds.length > 2) {
          showCallout.call(this);
        } else {
          let mapTypeId = getOtherMapTypeId.call(this, map.getMapTypeId());
          map.setMapTypeId(mapTypeId);
        }
      }
    }
    function onButtonClick() {
      showCallout.call(this);
    }
    function updateMode() {
      let previousMode = this.$mode,
        map = this.getMap(),
        style = this.getStyle();
      switch (style) {
        case MapTypeControlStyle.BUTTON:
        case MapTypeControlStyle.OVERVIEW:
          this.$mode = style;
          break;
        case MapTypeControlStyle.DEFAULT:
        default:
          if (map && mapContainerWidth(map) <= breakSizeDefault) {
            this.$mode = MapTypeControlStyle.BUTTON;
          } else {
            this.$mode = MapTypeControlStyle.OVERVIEW;
          }
          break;
      }
      let changed = this.$mode !== previousMode;
      if (changed) {
        switch (previousMode) {
          case MapTypeControlStyle.OVERVIEW:
            removeOverview.call(this);
            break;
          case MapTypeControlStyle.BUTTON:
            removeButton.call(this);
            break;
        }
        switch (this.$mode) {
          case MapTypeControlStyle.OVERVIEW:
            addOverview.call(this);
            break;
          case MapTypeControlStyle.BUTTON:
            addButton.call(this);
            break;
        }
        if (previousMode) {
          google.maps.event.trigger(this, "mode_changed", this.$mode);
        }
      }
      return changed;
    }
    function getOverviewMapType() {
      let newOverviewMapTypeId = null,
        mapTypeIds = this.getMapTypeIds(),
        overviewMapTypeId = this.getOverviewMapTypeId(),
        currentMapTypeId = this.getMap().getMapTypeId();
      if (overviewMapTypeId) {
        if (isFunction(overviewMapTypeId)) {
          newOverviewMapTypeId = overviewMapTypeId(currentMapTypeId, mapTypeIds, this.$previousMapTypeId);
        } else {
          newOverviewMapTypeId = overviewMapTypeId;
        }
      }
      if (!newOverviewMapTypeId && mapTypeIds && mapTypeIds.length > 2 && this.$previousMapTypeId) {
        newOverviewMapTypeId = mapTypeIds.find(id => id === this.$previousMapTypeId);
      }
      if (!newOverviewMapTypeId) {
        newOverviewMapTypeId = getOtherMapTypeId.call(this, currentMapTypeId);
      }
      switch (newOverviewMapTypeId) {
        case google.maps.MapTypeId.HYBRID:
          if (!mapTypeIds.some(id => id === google.maps.MapTypeId.SATELLITE)) {
            newOverviewMapTypeId = google.maps.MapTypeId.SATELLITE;
          }
          break;
      }
      return newOverviewMapTypeId;
    }
    function getOtherMapTypeId(mapTypeId) {
      let mapTypeIds = this.getMapTypeIds(),
        otherMapTypeIds = mapTypeIds.filter(id => id !== mapTypeId);
      if (otherMapTypeIds.length > 1) {
        let mapTypes = this.getMap().mapTypes,
          provider = getProvider(mapTypeId, mapTypes);
        if (provider) {
          let sameProvider = otherMapTypeIds.filter(id => getProvider(id, mapTypes) === provider);
          if (sameProvider.length) {
            //TODO: look for opposite (road vs satellite) first?
            return sameProvider[0];
          }
        } else if (this.$listeners) {
          this.$listeners.push(mapTypes.addListener(mapTypeId + "_changed", this.$queueRefresh));
        }
      }
      return otherMapTypeIds[0];
    }
    function getProvider(mapTypeId, mapTypes) {
      let mapType = mapTypes.get(mapTypeId);
      return mapType ? mapType.provider || MapProviderType.GOOGLE : null;
    }
    function updateOverviewMapText() {
      let text = null;
      let mapTypeIds = this.getMapTypeIds();
      if (mapTypeIds && mapTypeIds.length === 2) {
        let mapTypeId = this.$overview.getMapTypeId(),
          mapType = this.getMap().mapTypes.get(mapTypeId);
        if (mapType) {
          text = mapType.name;
        }
        let wait = false;
        if (!text) {
          switch (mapTypeId) {
            case MapTypeId.LIGHT:
            case MapTypeId.DARK:
            case google.maps.MapTypeId.ROADMAP:
              text = strings["maptypes.map"];
              wait = true;
              break;
            case google.maps.MapTypeId.SATELLITE:
              text = strings["maptypes.satellite"];
              wait = true;
              break;
            case google.maps.MapTypeId.HYBRID:
              if (getOtherMapTypeId.call(this, mapTypeId) !== google.maps.MapTypeId.SATELLITE) {
                text = strings["maptypes.satellite"];
                wait = true;
              }
              break;
          }
        }
        if (!text) {
          if (wait) {
            stringsPromise.then(proxy(updateOverviewMapText, this));
          } else {
            proxyTimeout(updateOverviewMapText, this, 100);
          }
        }
      }
      this.$overview.setText(text || null);
    }
    function onMapTypeControlOptionsChanged() {
      let map = this.getMap(),
        options = map ? map.get("mapTypeControlOptions") : null;
      if (options) {
        this.setOptions(options);
      }
    }
    function updateMapTypeIds() {
      hideCallout.call(this);
      let map = this.getMap(),
        mapTypeIds = this.getMapTypeIds() || this.get("defaultMapTypeIds");
      if (map && mapTypeIds) {
        let options = Object.assign({}, map.get("mapTypeControlOptions") || {});
        if (!arrayEquals(options.mapTypeIds, mapTypeIds)) {
          options.mapTypeIds = mapTypeIds;
          map.set("mapTypeControlOptions", options);
        }
        mapTypeIds.forEach(mapTypeId => {
          if (mapTypeId === MapTypeId.LIGHT && !map.mapTypes.get(mapTypeId)) {
            new LightMapType({
              map
            });
          }
        });
        let mapTypeId = map.getMapTypeId();
        clearTimeout(this.$initTimer);
        if (mapTypeIds.length && !mapTypeIds.includes(mapTypeId)) {
          this.$initTimer = setTimeout(() => {
            if (map === this.getMap() && mapTypeIds === this.getMapTypeIds() && mapTypeId === map.getMapTypeId()) {
              map.setMapTypeId(mapTypeIds[0]);
            }
          });
        }
        if (mapTypeIds.length > 2) {
          addCallout.call(this);
        }
      }
    }
    function updateClassName() {
      if (this.$div) {
        let className = this.get("className");
        this.$div.className = classNames(className, controlClass(), noPrintClass);
      }
    }
    function updatePosition() {
      hideCallout.call(this);
      let position = this.get("position");
      repositionMapControl(this.getMap(), this.$div, position);
    }
    function showCallout() {
      if (this.$callout) {
        this.$callout.setOptions({
          target: this.getDiv(),
          mapTypeIds: this.getMapTypeIds(),
          map: this.getMap()
        });
        this.$callout.show();
      }
    }
    function hideCallout() {
      if (this.$callout) {
        this.$callout.hide();
      }
    }
    return {
      $queueRefresh: noop,
      refresh: function () {
        hideCallout.call(this);
        let mapTypeIds = this.getMapTypeIds();
        if (this.$mode && this.getMap() && mapTypeIds) {
          switch (this.$mode) {
            case MapTypeControlStyle.OVERVIEW:
              if (this.$overview) {
                let overviewMapType = getOverviewMapType.call(this);
                this.$overview.setMapTypeId(overviewMapType);
                updateOverviewMapText.call(this);
              }
              break;
          }
        }
      },
      getMode: function () {
        return this.$mode;
      },
      getDiv: function () {
        switch (this.$mode) {
          case MapTypeControlStyle.OVERVIEW:
            return this.$overview ? this.$overview.getDiv() : null;
          case MapTypeControlStyle.BUTTON:
            return this.$div || null;
        }
      },
      changed: function (key) {
        switch (key) {
          case "map":
            if (this.getMap()) {
              onAdd.call(this);
            } else {
              onRemove.call(this);
            }
            break;
          case "mapTypeIds":
            updateMapTypeIds.call(this);
            updateMode.call(this);
            this.$queueRefresh();
            break;
          case "style":
            updateMode.call(this);
            this.$queueRefresh();
            break;
          case "overviewMapTypeId":
            this.$queueRefresh();
            break;
          case "position":
            updatePosition.call(this);
            break;
          case "className":
            updateClassName.call(this);
            break;
        }
      }
    };
  })()));
};
export default MapTypeControl;
export { MapTypeControlStyle };